import { OrganigramComponent } from './../organigram-component';
import { Gui } from './../../../gui';
import { ColorConfig } from './../../../color-config';
import { EmployeeDataType } from './../../../../data-management/data-sources/sources/employees-data-source';
import { NodeGroup } from './../layout/node-group';
import { VisualNode } from './../visual/visual-node';
import { SelectAccountCommandParameters } from '../../../commands/select-account-command-executor';
import { FilterCommandParameters } from '@/gui/commands/filter-command-executor';
import { LayerManager } from './layer-manager';

interface TeamDataType {
    id: string;
    employees: EmployeeDataType[];
    accountName: string;
    accountClusterId: string;
}

export class TeamNode extends VisualNode {
    private svgText: any;
    private minTextSize: number = 10;

    public enabled: boolean = false;
    public selected: boolean = false;
    public zIndex: number = LayerManager.zIndexTeamNode;

    protected svgCircle: any;

    constructor(protected data: TeamDataType, private organigram: OrganigramComponent, private gui: Gui) {
        super(new NodeGroup());

        this.gui.commandManager.selectAccountCommandExecutor.commandExecuted.attach(
            (parameters: SelectAccountCommandParameters) => {
                this.checkSelected(parameters);
            }
        );
        this.gui.commandManager.selectUserCommandExecutor.commandExecuted.attach(() => {
            if (this.selected) {
                this.deselect();
            }
        });
        this.gui.commandManager.filterCommandExecutor.commandExecuted.attach((parameters: FilterCommandParameters) => {
            this.checkEnabled(parameters);
        });
    }

    protected redraw() {
        const factorToCreateCircleWithControlPoints = 1.4;
        const outerCircleWidth = this.layoutNode.pixelR;
        const x1 = this.layoutNode.pixelX - outerCircleWidth;
        const y1 = this.layoutNode.pixelY;
        const cx1 = this.layoutNode.pixelX - outerCircleWidth;
        const cy1 = this.layoutNode.pixelY - factorToCreateCircleWithControlPoints * outerCircleWidth;
        const cx2 = this.layoutNode.pixelX + outerCircleWidth;
        const cy2 = this.layoutNode.pixelY - factorToCreateCircleWithControlPoints * outerCircleWidth;
        const x2 = this.layoutNode.pixelX + outerCircleWidth;
        const y2 = this.layoutNode.pixelY;
        const path = 'M' + x1 + ' ' + y1 + ' C ' + cx1 + ' ' + cy1 + ',' + cx2 + ' ' + cy2 + ',' + x2 + ' ' + y2;
        this.svgText.attr({
            fill: ColorConfig.organigramTeamText,
            textpath: path,
            'font-size': Math.max(this.minTextSize, this.layoutNode.margin * outerCircleWidth * 1.2)
        });
        this.svgCircle.attr({
            cx: this.layoutNode.pixelX,
            cy: this.layoutNode.pixelY,
            r: this.layoutNode.pixelR
        });
    }

    protected initdraw() {
        this.svgCircle = this.organigram.snap.circle(
            this.layoutNode.pixelX,
            this.layoutNode.pixelY,
            this.layoutNode.pixelR
        );
        this.svgText = this.organigram.snap.text(0, 0, this.data.accountName);

        this.svgCircle.click((e: MouseEvent) => this.handleClick(e));
        this.svgCircle.mouseover(() => this.handleHover());
        this.svgCircle.mouseout(() => this.handleOut());
        this.svgText.click((e: MouseEvent) => this.handleClick(e));
        this.redraw();
        this.disable();
    }

    protected handleHover() {
        this.svgCircle.attr({
            stroke: ColorConfig.organigramTeamCircleBorderSelected,
            strokeWidth: 2
        });
    }

    protected handleOut() {
        if (!this.selected && !this.enabled) {
            this.svgCircle.attr({
                fill: ColorConfig.hidden,
                stroke: ColorConfig.organigramTeamCircleBorder,
                strokeWidth: 1
            });
        } else if (this.selected) {
            this.svgCircle.attr({
                fill: ColorConfig.selectedTransparent,
                stroke: ColorConfig.organigramTeamCircleBorderSelected,
                strokeWidth: 1
            });
        } else {
            this.svgCircle.attr({
                fill: ColorConfig.filterResultDarkTransparent,
                stroke: ColorConfig.organigramConsultantEnabledBorder,
                strokeWidth: 1
            });
        }
    }

    private handleClick(e: MouseEvent) {
        this.gui.commandManager.selectAccountCommandExecutor.requestExecute({
            accountId: this.data.id,
            enable: !this.selected
        });
        e.stopPropagation();
    }

    private checkSelected(parameters: SelectAccountCommandParameters) {
        if (parameters.accountId === this.data.id && parameters.enable) {
            this.select();
        } else {
            if (this.selected) {
                this.deselect();
                if (this.enabled) {
                    this.enable();
                }
            }
        }
    }

    private select() {
        this.selected = true;
        this.svgCircle.attr({
            fill: ColorConfig.selectedTransparent,
            stroke: ColorConfig.organigramTeamCircleBorderSelected,
            strokeWidth: 1
        });
        this.svgText.attr({
            fill: ColorConfig.organigramTeamTextSelected,
            'font-weight': 900
        });
    }

    private deselect() {
        this.selected = false;
        this.svgCircle.attr({
            fill: ColorConfig.hidden,
            stroke: ColorConfig.organigramTeamCircleBorder,
            strokeWidth: 1
        });
        this.svgText.attr({
            fill: ColorConfig.organigramTeamText,
            'font-weight': 700
        });
    }

    private checkEnabled(parameters: FilterCommandParameters) {
        if (
            (parameters.filter.accounts &&
                parameters.filter.accounts.length &&
                parameters.filter.accounts.some(accountFilter => accountFilter.value === this.data.id)) ||
            (parameters.filter.accountClusters &&
                parameters.filter.accountClusters.length &&
                parameters.filter.accountClusters.some(accountClusterFilter => accountClusterFilter.value === this.data.accountClusterId))
        ) {
            this.enable();
        } else {
            this.disable();
            if (this.selected) {
                this.deselect();
            }
        }
    }

    private enable() {
        this.enabled = true;

        this.svgCircle.attr({
            fill: ColorConfig.filterResultDarkTransparent,
            stroke: ColorConfig.organigramTeamCircleBorderFilterResult,
            strokeWidth: 1
        });
        this.svgText.attr({
            fill: ColorConfig.organigramTeamTextFilterResult,
            'font-weight': '900'
        });
    }

    private disable() {
        this.enabled = false;

        this.svgCircle.attr({
            fill: ColorConfig.hidden,
            stroke: ColorConfig.organigramTeamCircleBorder,
            strokeWidth: 1
        });

        this.svgText.attr({
            fill: ColorConfig.organigramTeamText,
            'font-weight': '700'
        });
    }
}
